import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Card, Checkbox, Form, Input, message, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { LoginContext } from "../../../context/loginContext";

const { Text, Title } = Typography;

const UsuariosAdministrador = () => {
  const { user } = useContext(LoginContext);
  const [usuarios, setUsuarios] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const api = process.env.REACT_APP_API_URL;
  const authHeaders = { Authorization: `Bearer ${user?.token}` };

  const loadData = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const [u, t, e] = await Promise.all([
        axios.get(`${api}/usuarios/admin/usuarios`, { headers: authHeaders }),
        axios.get(`${api}/usuarios/admin/tipos-usuario`, { headers: authHeaders }),
        axios.get(`${api}/usuarios/admin/empresas`, { headers: authHeaders }),
      ]);
      setUsuarios(Array.isArray(u.data) ? u.data : []);
      setTipos(Array.isArray(t.data) ? t.data : []);
      setEmpresas(Array.isArray(e.data) ? e.data : []);
    } catch (err) {
      message.error(err.response?.data?.message || "No se pudieron cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, [api, user?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCrear = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ empresas: [] });
    setOpen(true);
  };

  const openEditar = async (row) => {
    setEditing(row);
    try {
      const { data } = await axios.get(`${api}/usuarios/admin/usuario/${row.usu_id}/empresas`, {
        headers: authHeaders,
      });
      form.setFieldsValue({
        usuario: row.usu_usuario,
        tipoUsuario: row.usu_tipousuario || undefined,
        empresas: Array.isArray(data) ? data : [],
        contrasenia: "",
      });
      setOpen(true);
    } catch (err) {
      message.error(err.response?.data?.message || "No se pudieron cargar empresas del usuario");
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const body = {
      usuario: values.usuario,
      tipoUsuario: Number(values.tipoUsuario),
      empresas: Array.isArray(values.empresas) ? values.empresas : [],
    };
    if (values.contrasenia && String(values.contrasenia).trim()) {
      body.contrasenia = values.contrasenia;
    }

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${api}/usuarios/${editing.usu_id}`, body, { headers: authHeaders });
        message.success("Usuario actualizado");
      } else {
        if (!body.contrasenia) {
          message.warning("Ingresá una contraseña para crear el usuario");
          return;
        }
        await axios.post(`${api}/usuarios`, body, { headers: authHeaders });
        message.success("Usuario creado");
      }
      setOpen(false);
      form.resetFields();
      setEditing(null);
      loadData();
    } catch (err) {
      message.error(err.response?.data?.message || "No se pudo guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "Usuario", dataIndex: "usu_usuario", key: "usu_usuario" },
    {
      title: "Rol",
      key: "rol",
      render: (_, r) => (
        <Tag color={r.tiu_descripcion === "Administrador" ? "blue" : "default"}>
          {r.tiu_descripcion || `Tipo ${r.usu_tipousuario || "-"}`}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, r) => (
        <Button icon={<EditOutlined />} onClick={() => openEditar(r)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Title level={5} style={{ margin: 0 }}>
            Usuarios
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCrear}>
            Nuevo usuario
          </Button>
        </Space>
      }
    >
      <Text type="secondary">
        Creá o editá usuarios, definí su rol y seleccioná las empresas que puede ver.
      </Text>
      <Table
        rowKey="usu_id"
        style={{ marginTop: 12 }}
        loading={loading}
        columns={columns}
        dataSource={usuarios}
        pagination={{ pageSize: 15 }}
      />

      <Modal
        title={editing ? "Editar usuario" : "Nuevo usuario"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="usuario" label="Usuario" rules={[{ required: true, message: "Obligatorio" }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="contrasenia"
            label={editing ? "Contraseña (opcional para cambiar)" : "Contraseña"}
            rules={editing ? [] : [{ required: true, message: "Obligatorio" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="tipoUsuario" label="Rol" rules={[{ required: true, message: "Obligatorio" }]}>
            <Select
              options={tipos.map((t) => ({
                value: t.tiu_id,
                label: t.tiu_descripcion || `Tipo ${t.tiu_id}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="empresas" label="Empresas visibles">
            <Checkbox.Group style={{ width: "100%" }}>
              <div
                style={{
                  maxHeight: 220,
                  overflowY: "auto",
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: "#fafafa",
                }}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  {empresas.map((e) => (
                    <Checkbox key={e.emp_id} value={e.emp_id}>
                      {e.emp_nombre} ({e.emp_id})
                    </Checkbox>
                  ))}
                </Space>
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default UsuariosAdministrador;
